declare global {
    interface Window {
        Cashfree?: (options: { mode: 'sandbox' | 'production' }) => {
            checkout: (options: {
                paymentSessionId: string;
                redirectTarget?: '_modal' | '_self' | '_top';
            }) => Promise<{
                error?: { message: string; code?: string };
                redirect?: boolean;
                paymentDetails?: any;
            }>;
        };
    }
}

let cashfreeScriptPromise: Promise<boolean> | null = null;

/**
 * Dynamically loads the official Cashfree JS SDK v3
 */
export const loadCashfreeSDK = (): Promise<boolean> => {
    if (typeof window !== 'undefined' && window.Cashfree) {
        return Promise.resolve(true);
    }

    if (cashfreeScriptPromise) {
        return cashfreeScriptPromise;
    }

    cashfreeScriptPromise = new Promise<boolean>((resolve) => {
        const existingScript = document.querySelector('script[src="https://sdk.cashfree.com/js/v3/cashfree.js"]');
        if (existingScript) {
            existingScript.addEventListener('load', () => resolve(true));
            existingScript.addEventListener('error', () => resolve(false));
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
        script.async = true;
        script.onload = () => resolve(true);
        script.onerror = () => {
            console.error('[Cashfree] Failed to load Cashfree JS SDK script');
            resolve(false);
        };
        document.body.appendChild(script);
    });

    return cashfreeScriptPromise;
};

/**
 * Launches Cashfree Checkout modal
 */
export const openCashfreeCheckout = async (params: {
    paymentSessionId: string;
    environment?: 'sandbox' | 'production';
    redirectTarget?: '_modal' | '_self';
}): Promise<{ success: boolean; error?: string; raw?: any }> => {
    const { paymentSessionId, environment = 'sandbox', redirectTarget = '_modal' } = params;

    const isLoaded = await loadCashfreeSDK();
    if (!isLoaded || !window.Cashfree) {
        throw new Error('Cashfree Payment Gateway SDK could not be loaded. Please check your internet connection.');
    }

    try {
        const cashfree = window.Cashfree({ mode: environment });
        const result = await cashfree.checkout({
            paymentSessionId,
            redirectTarget,
        });

        if (result?.error) {
            return {
                success: false,
                error: result.error.message || 'Payment was canceled or failed in Cashfree gateway.',
                raw: result.error,
            };
        }

        return {
            success: true,
            raw: result,
        };
    } catch (err: any) {
        console.error('[Cashfree] Checkout execution error:', err);
        return {
            success: false,
            error: err.message || 'Payment popup was closed or encountered an error.',
        };
    }
};
